import React, { useState } from 'react';
import DefaultWhiteColumn from '~/features/base/components/DefaultWhiteColumn';
import { Campaign } from '~/api/models/campaign';
import { useForm } from 'react-hook-form';
import CampaignStatus from './CampaignStatus';
import CampaignUpdateEvents from './CampaignUpdateEvents';
import CampaignDetailsSection from './CampaignDetailsSection';
import CampaignTargetDeliverableSection from './CampaignTargetDeliverableSection';
import CampaignTargetVehiclesSection from './CampaignTargetVehiclesSection';
import { toShortSemanticVersion } from '~/features/base/utils/versionNumberConverter';
import EditCampaignSectionDialog from '~/features/campaigns/components/campaignForm/EditCampaignSectionDialog';
import { useCampaign } from '../../../hooks/useCampaign';

type SectionType = 'details' | 'deliverable' | 'vehicles';

interface CampaignOverviewContainerProps {
    campaign?: Campaign;
}

const CampaignOverviewContainer: React.FC<CampaignOverviewContainerProps> = ({ campaign: initialCampaign }) => {
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [currentSection, setCurrentSection] = useState<SectionType>('details');
    const { campaign: currentCampaign, fetchCampaign } = useCampaign(initialCampaign);

    // Form setup for the edit dialogs
    const { control, setValue, watch, reset } = useForm({
        defaultValues: {
            // Campaign details
            campaignName: currentCampaign?.name || '',
            campaignManager: currentCampaign?.manager || '',
            deviceType: currentCampaign?.deviceType || '',
            decisionBoardUrl: currentCampaign?.decisionBoard || '',
            description: currentCampaign?.description || '',

            // Target deliverable
            deliverableType: currentCampaign?.deliverableType || '',
            deliverableId: currentCampaign?.deliverableId || '',
            deliverableVersion: toShortSemanticVersion(currentCampaign?.deliverableVersion) || '',

            // Target vehicles
            targetGroups: currentCampaign?.vehicleGroup || ''
        }
    });

    const formValues = watch();

    const handleOpenDialog = (section: SectionType) => {
        setCurrentSection(section);
        setShowEditDialog(true);
    };

    const handleCloseDialog = () => {
        // Reset form to default values
        reset({
            // Campaign details
            campaignName: currentCampaign?.name || '',
            campaignManager: currentCampaign?.manager || '',
            deviceType: currentCampaign?.deviceType || '',
            decisionBoardUrl: currentCampaign?.decisionBoard || '',
            description: currentCampaign?.description || '',

            // Target deliverable
            deliverableType: currentCampaign?.deliverableType || '',
            deliverableId: currentCampaign?.deliverableId || '',
            deliverableVersion: toShortSemanticVersion(currentCampaign?.deliverableVersion) || '',

            // Target vehicles
            targetGroups: currentCampaign?.vehicleGroup || ''
        });
        setShowEditDialog(false);
    };

    if (!currentCampaign) {
        return <div>Loading campaign...</div>;
    }

    return (
        <DefaultWhiteColumn className='padding-50'>
            <div className='display-flex margin-top-20 gap-25'>
                <div className='width-33pct'>
                    <CampaignStatus campaign={currentCampaign}/>
                </div>
                <div className=' flex-1-1-0'>
                    <CampaignUpdateEvents campaign={currentCampaign}/>
                </div>
            </div>

            {/* Details and Target Deliverable sections */}
            <div className='row margin-top-50'>
                <div className='col-6'>
                    <CampaignDetailsSection
                        campaign={currentCampaign}
                        onEditClick={() => handleOpenDialog('details')}
                    />
                </div>
                <div className='col-6 '>
                    <CampaignTargetDeliverableSection
                        campaign={currentCampaign}
                        onEditClick={() => handleOpenDialog('deliverable')}
                    />

                    {/* Target Vehicles section directly below Target Deliverable */}
                    <div className='margin-top-20'>
                        <CampaignTargetVehiclesSection
                            campaign={currentCampaign}
                            onEditClick={() => handleOpenDialog('vehicles')}
                        />
                    </div>
                </div>
            </div>

            <EditCampaignSectionDialog
                show={showEditDialog}
                onHide={handleCloseDialog}
                campaign={currentCampaign}
                control={control}
                setValue={setValue}
                formValues={formValues}
                sectionType={currentSection}
                reset={reset}
                onSave={fetchCampaign}
            />
        </DefaultWhiteColumn>
    );
};

export default CampaignOverviewContainer;
